import LumaAccessiblePanel from '../../../components/admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState, useEffect, useRef } from 'react';
import { X, Shield, Camera, KeyRound, Image as ImageIcon, UserCheck } from 'lucide-react';
import {
    updateCorrespondencia,
    Correspondencia,
    CorrespondenciaUpdate,
    marcarComoNotificado,
    marcarComoEntregado,
    cancelarCorrespondencia
} from '../../../services/correspondencia';
import ValidarEntregaModal from './ValidarEntregaModal';

interface EditarCorrespondenciaModalProps {
    isOpen: boolean;
    onClose: () => void;
    correspondencia: Correspondencia;
    onSave: () => void;
}

const EditarCorrespondenciaModal: React.FC<EditarCorrespondenciaModalProps> = ({
    isOpen,
    onClose,
    correspondencia,
    onSave
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [pinIngresado, setPinIngresado] = useState('');
    const [mostrarModalEntrega, setMostrarModalEntrega] = useState(false);
    const [imagenEntregaFile, setImagenEntregaFile] = useState<File | null>(null);
    const [imagenEntregaPreview, setImagenEntregaPreview] = useState<string | null>(null);
    const [verFotoModal, setVerFotoModal] = useState<string | null>(null);
    const entregaFileInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState<CorrespondenciaUpdate>({
        estado: correspondencia.estado,
        descripcion: correspondencia.descripcion || '',
        receptor_nombre: correspondencia.receptor_nombre || '',
        receptor_apellido: correspondencia.receptor_apellido || '',
        receptor_telefono: correspondencia.receptor_telefono || '',
        receptor_ci_nit: correspondencia.receptor_ci_nit || ''
    });

    // Campos adicionales para editar remitente
    const [remitenteData, setRemitenteData] = useState({
        remitente_nombre: correspondencia.remitente_nombre,
        remitente_apellido: correspondencia.remitente_apellido,
        remitente_telefono: correspondencia.remitente_telefono,
        remitente_ci_nit: correspondencia.remitente_ci_nit
    });

    useEffect(() => {
        if (isOpen && correspondencia) {
            setFormData({
                estado: correspondencia.estado,
                descripcion: correspondencia.descripcion || '',
                receptor_nombre: correspondencia.receptor_nombre || '',
                receptor_apellido: correspondencia.receptor_apellido || '',
                receptor_telefono: correspondencia.receptor_telefono || '',
                receptor_ci_nit: correspondencia.receptor_ci_nit || ''
            });

            setRemitenteData({
                remitente_nombre: correspondencia.remitente_nombre,
                remitente_apellido: correspondencia.remitente_apellido,
                remitente_telefono: correspondencia.remitente_telefono,
                remitente_ci_nit: correspondencia.remitente_ci_nit
            });
            setPinIngresado('');
            setMostrarModalEntrega(false);
            setImagenEntregaFile(null);
            setImagenEntregaPreview(null);
            setVerFotoModal(null);
        }
    }, [isOpen, correspondencia]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRemitenteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setRemitenteData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImagenEntregaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                alert('La imagen no debe superar los 10MB');
                return;
            }
            setImagenEntregaFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagenEntregaPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await updateCorrespondencia(correspondencia.id, {
                ...formData,
                ...remitenteData
            });

            alert('Correspondencia actualizada exitosamente');
            onSave();
            onClose();
        } catch (error: any) {
            /* Browser diagnostics omitted. */

            if (error.response) {
                const errorData = error.response.data;
                let errorMessage = 'Error al actualizar correspondencia:\n';

                if (errorData.detail) {
                    errorMessage += errorData.detail;
                } else if (typeof errorData === 'string') {
                    errorMessage += errorData;
                } else {
                    for (const [field, messages] of Object.entries(errorData)) {
                        errorMessage += `- ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}\n`;
                    }
                }

                alert(errorMessage);
            } else if (error.request) {
                alert('No se recibió respuesta del servidor. Verifique su conexión.');
            } else {
                alert(`Error: ${error.message}`);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmarEntrega = async () => {
        if (!formData.receptor_nombre || !formData.receptor_ci_nit) {
            alert('Para marcar como entregado, debe completar el Nombre y CI/NIT del receptor.');
            return;
        }

        if (!pinIngresado.trim()) {
            alert('Debe ingresar el PIN de seguridad de retiro proporcionado por el cliente.');
            return;
        }

        setIsLoading(true);
        try {
            const updated = await marcarComoEntregado(correspondencia.id, {
                receptor_nombre: formData.receptor_nombre || '',
                receptor_apellido: formData.receptor_apellido || '',
                receptor_telefono: formData.receptor_telefono || '',
                receptor_ci_nit: formData.receptor_ci_nit || '',
                pin_seguridad: pinIngresado.trim(),
                imagen_entrega: imagenEntregaFile
            });

            setFormData(prev => ({
                ...prev,
                estado: updated.estado
            }));

            alert('Correspondencia entregada y validada exitosamente con PIN de seguridad.');
            setMostrarModalEntrega(false);
            onSave();
            onClose();
        } catch (error: any) {
            /* Browser diagnostics omitted. */
            const msg = error.response?.data?.detail || 'Error al validar entrega. Verifique el PIN de seguridad.';
            alert(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEstadoChange = async (nuevoEstado: 'recibido' | 'notificado' | 'entregado' | 'cancelado') => {
        if (nuevoEstado === 'entregado') {
            setMostrarModalEntrega(true);
            return;
        }

        setIsLoading(true);
        try {
            let updatedCorrespondencia: Correspondencia;

            switch (nuevoEstado) {
                case 'notificado':
                    updatedCorrespondencia = await marcarComoNotificado(correspondencia.id);
                    break;
                case 'cancelado':
                    updatedCorrespondencia = await cancelarCorrespondencia(correspondencia.id);
                    break;
                default:
                    updatedCorrespondencia = await updateCorrespondencia(correspondencia.id, { estado: nuevoEstado });
            }

            setFormData(prev => ({
                ...prev,
                estado: updatedCorrespondencia.estado
            }));

            alert(`Estado cambiado a ${getEstadoTexto(nuevoEstado)} exitosamente`);
            onSave();
        } catch (error: any) {
            /* Browser diagnostics omitted. */
            const errorMsg = error.response?.data?.detail || `Error al cambiar estado: ${error.message}`;
            alert(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const getEstadoTexto = (estado: string) => {
        switch (estado) {
            case 'recibido': return 'Recibido';
            case 'notificado': return 'Notificado';
            case 'entregado': return 'Entregado';
            case 'cancelado': return 'Cancelado';
            default: return estado;
        }
    };

    const getEstadoColor = (estado: string | undefined) => {
        if (!estado) return 'bg-gray-100 text-gray-800';
        switch (estado) {
            case 'recibido':
                return 'bg-blue-100 text-blue-800';
            case 'notificado':
                return 'bg-yellow-100 text-yellow-800';
            case 'entregado':
                return 'bg-green-100 text-green-800';
            case 'cancelado':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <style>{`
                .editar-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .editar-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .editar-scroll::-webkit-scrollbar-thumb {
                    background-color: rgba(0, 127, 200, 0.3);
                    border-radius: 20px;
                }
                .editar-scroll::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(0, 127, 200, 0.5);
                }
                .editar-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(0, 127, 200, 0.3) transparent;
                }
            `}</style>

            {/* Modal unificado de validación de entrega con PIN y Foto en vivo / archivo */}
            {mostrarModalEntrega && (
                <ValidarEntregaModal
                    isOpen={mostrarModalEntrega}
                    correspondencia={correspondencia}
                    onClose={() => setMostrarModalEntrega(false)}
                    onSuccess={(updated) => {
                        setFormData(prev => ({
                            ...prev,
                            estado: updated.estado
                        }));
                        setMostrarModalEntrega(false);
                        alert('¡Correspondencia entregada y validada con PIN exitosamente!');
                        onSave();
                        onClose();
                    }}
                />
            )}

            {/* Modal de foto en pantalla completa */}
            {verFotoModal && (
                <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true"
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[80] p-4 cursor-pointer"
                    onClick={() => setVerFotoModal(null)}
                >
                    <LumaAccessiblePanel data-luma-dialog="true" className="relative max-w-3xl max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-2xl p-2" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setVerFotoModal(null)}
                            className="absolute top-4 right-4 p-2 bg-black/60 text-white rounded-full hover:bg-black transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <img src={verFotoModal} alt="Foto Ampliada" className="max-w-full max-h-[80vh] object-contain rounded-xl" />
                    </LumaAccessiblePanel>
                </LumaAccessibleDialog>
            )}

            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto editar-scroll">
                    <div className="p-8">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <div className="flex items-center gap-3">
                                    <LumaAccessibleTitle className="text-2xl font-bold text-gray-800">Editar Correspondencia</LumaAccessibleTitle>
                                    {correspondencia.codigo && (
                                        <span className="px-3 py-1 text-sm font-mono font-bold bg-blue-100 text-[#3564db] rounded-lg border border-blue-200">
                                            {correspondencia.codigo}
                                        </span>
                                    )}
                                </div>
                                <p className="text-gray-600 mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                                    <span>Cita: <span className="font-semibold text-[#3564db]">{correspondencia.cita_codigo}</span></span>
                                    <span className="text-gray-300">•</span>
                                    <span>Destinatario: <span className="font-semibold text-gray-700">{correspondencia.cliente_nombre || 'Cliente'}</span></span>
                                    <span className="text-gray-300">•</span>
                                    <span className="inline-flex items-center gap-1 text-gray-800 bg-blue-50 px-2.5 py-0.5 rounded-lg border border-blue-100 font-medium text-xs">
                                        <UserCheck className="w-3.5 h-3.5 text-[#3564db]" />
                                        Recepcionista: <strong>{correspondencia.usuario_recibe_nombre || 'Recepción Comarca'}</strong>
                                    </span>
                                    {correspondencia.estado === 'entregado' && correspondencia.usuario_entrega_nombre && (
                                        <>
                                            <span className="text-gray-300">•</span>
                                            <span className="inline-flex items-center gap-1 text-emerald-800 bg-emerald-50 px-2.5 py-0.5 rounded-lg border border-emerald-100 font-medium text-xs">
                                                Entregó: <strong>{correspondencia.usuario_entrega_nombre}</strong>
                                            </span>
                                        </>
                                    )}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-8"><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                            {/* Respaldo Fotográfico Existente */}
                            {correspondencia.imagen && (
                                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={correspondencia.imagen}
                                            alt="Respaldo"
                                            className="w-16 h-16 object-cover rounded-lg border border-blue-200 shadow-sm cursor-pointer hover:opacity-90"
                                            onClick={() => setVerFotoModal(correspondencia.imagen || null)}
                                        />
                                        <div>
                                            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider block">Foto de Respaldo al Recibir</span>
                                            <p className="text-xs text-gray-500 mt-0.5">Adjuntada en recepción</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setVerFotoModal(correspondencia.imagen || null)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#3564db] bg-white hover:bg-blue-50 border border-blue-200 rounded-lg shadow-sm"
                                    >
                                        <ImageIcon className="w-3.5 h-3.5" />
                                        Ver Foto Completa
                                    </button>
                                </div>
                            )}

                            {/* Datos del Remitente */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-[#3564db]/10 to-[#92b1ed]/10 rounded-lg flex items-center justify-center">
                                        <div className="w-3 h-3 bg-[#3564db] rounded-full"></div>
                                    </div>
                                    Datos del Remitente
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Nombre *
                                        </label>
                                        <input
                                            type="text"
                                            name="remitente_nombre"
                                            value={remitenteData.remitente_nombre}
                                            onChange={handleRemitenteChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                            placeholder="Ej: Juan"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Apellido *
                                        </label>
                                        <input
                                            type="text"
                                            name="remitente_apellido"
                                            value={remitenteData.remitente_apellido}
                                            onChange={handleRemitenteChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                            placeholder="Ej: Pérez"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Teléfono *
                                        </label>
                                        <input
                                            type="tel"
                                            name="remitente_telefono"
                                            value={remitenteData.remitente_telefono}
                                            onChange={handleRemitenteChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                            placeholder="Ej: 71234567"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            CI/NIT *
                                        </label>
                                        <input
                                            type="text"
                                            name="remitente_ci_nit"
                                            value={remitenteData.remitente_ci_nit}
                                            onChange={handleRemitenteChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                            placeholder="Ej: 1234567 o 123456789"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Datos del Receptor */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-[#3564db]/10 to-[#92b1ed]/10 rounded-lg flex items-center justify-center">
                                        <div className="w-3 h-3 bg-[#92b1ed] rounded-full"></div>
                                    </div>
                                    Datos del Receptor (Persona que Retira)
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Nombre
                                        </label>
                                        <input
                                            type="text"
                                            name="receptor_nombre"
                                            value={formData.receptor_nombre || ''}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92b1ed]/30 focus:border-[#92b1ed] transition-all"
                                            placeholder="Ej: María"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Apellido
                                        </label>
                                        <input
                                            type="text"
                                            name="receptor_apellido"
                                            value={formData.receptor_apellido || ''}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92b1ed]/30 focus:border-[#92b1ed] transition-all"
                                            placeholder="Ej: López"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Teléfono
                                        </label>
                                        <input
                                            type="tel"
                                            name="receptor_telefono"
                                            value={formData.receptor_telefono || ''}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92b1ed]/30 focus:border-[#92b1ed] transition-all"
                                            placeholder="Ej: 71234567"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            CI/NIT
                                        </label>
                                        <input
                                            type="text"
                                            name="receptor_ci_nit"
                                            value={formData.receptor_ci_nit || ''}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92b1ed]/30 focus:border-[#92b1ed] transition-all"
                                            placeholder="Ej: 1234567 o 123456789"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Descripción */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-[#3564db]/10 to-[#92b1ed]/10 rounded-lg flex items-center justify-center">
                                        <div className="w-3 h-3 bg-[#3564db] rounded-full"></div>
                                    </div>
                                    Descripción del Paquete / Documento
                                </h3>

                                <div className="space-y-3">
                                    <textarea
                                        name="descripcion"
                                        value={formData.descripcion || ''}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                        rows={3}
                                        placeholder="Describe el contenido de la correspondencia..."
                                    />
                                </div>
                            </div>

                            {/* Estado y Acciones */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-[#3564db]/10 to-[#92b1ed]/10 rounded-lg flex items-center justify-center">
                                        <div className="w-3 h-3 bg-[#3564db] rounded-full"></div>
                                    </div>
                                    Estado y Control de Entrega
                                </h3>

                                <div className="space-y-4">
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            type="button"
                                            onClick={() => handleEstadoChange('recibido')}
                                            disabled={isLoading || formData.estado === 'recibido'}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${formData.estado === 'recibido'
                                                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                        >
                                            Marcar como Recibido
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleEstadoChange('notificado')}
                                            disabled={isLoading || formData.estado === 'notificado'}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${formData.estado === 'notificado'
                                                ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                        >
                                            Marcar como Notificado
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleEstadoChange('entregado')}
                                            disabled={isLoading || formData.estado === 'entregado'}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${formData.estado === 'entregado'
                                                ? 'bg-green-100 text-green-800 border border-green-300'
                                                : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'}`}
                                        >
                                            <Shield className="w-4 h-4" />
                                            {formData.estado === 'entregado' ? 'Entregado' : 'Validar y Entregar (PIN)'}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleEstadoChange('cancelado')}
                                            disabled={isLoading || formData.estado === 'cancelado'}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${formData.estado === 'cancelado'
                                                ? 'bg-red-100 text-red-800 border border-red-300'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                        >
                                            Cancelar Correspondencia
                                        </button>
                                    </div>

                                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Estado actual</p>
                                                <p className={`text-base font-semibold mt-1 ${getEstadoColor(formData.estado)} px-3 py-1 rounded-full inline-block`}>
                                                    {getEstadoTexto(formData.estado || '')}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-600">
                                                    Fecha recepción: {new Date(correspondencia.fecha_recepcion).toLocaleDateString('es-BO')}
                                                </p>
                                                {correspondencia.fecha_entrega && (
                                                    <p className="text-xs text-green-600 font-medium mt-1">
                                                        Entregado: {new Date(correspondencia.fecha_entrega).toLocaleString('es-BO')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 pt-8 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200"
                                    disabled={isSubmitting || isLoading}
                                >
                                    Cerrar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || isLoading}
                                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#3564db] to-[#92b1ed] hover:shadow-lg rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {isSubmitting ? 'Guardando...' : 'Guardar Datos'}
                                </button>
                            </div>
                        </form>
                    </div>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>
    );
};

export default EditarCorrespondenciaModal;

